import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, ChevronRight, Search, Truck, Layers, Plus, Trash2, Pencil, X, Check, ExternalLink } from "lucide-react";

const STATUS = {
  moving: { l: "En mouvement", c: "#10B981" },
  idling: { l: "Ralenti", c: "#F59E0B" },
  stopped: { l: "À l'arrêt", c: "#EF4444" },
  offline: { l: "Hors ligne", c: "#71717A" },
};

const StatusBadge = ({ s }) => {
  const key = (s === "moving" || s === "idling" || s === "stopped") ? s : "stopped";
  const st = STATUS[key];
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: st.c }} data-testid={`veh-status-${s}`}>
    <span className="w-2 h-2 rounded-full" style={{ background: st.c }} />{st.l}</span>;
};

const VehicleRow = ({ v, poolNames, onOpen }) => (
  <div className="flex items-center gap-3 px-3 py-2 border-t border-border hover:bg-black/5 transition-colors" data-testid={`fleet-veh-${v.id}`}>
    <Truck className="w-4 h-4 text-muted-foreground shrink-0" />
    <span className="font-semibold text-sm min-w-0 truncate flex-1">{v.name}</span>
    <span className="text-xs text-muted-foreground data w-28 shrink-0">{v.plate}</span>
    <span className="w-28 shrink-0"><StatusBadge s={v.status} /></span>
    {v.model && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#17A2B8]/15 text-[#2ec5db] shrink-0">{v.model}</span>}
    {poolNames.map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5821F]/15 text-[#F5821F] flex items-center gap-1 shrink-0"><Layers className="w-2.5 h-2.5" />{p}</span>)}
    <button onClick={() => onOpen(v.id)} data-testid={`fleet-veh-open-${v.id}`} title="Ouvrir la fiche véhicule"
      className="ml-auto text-muted-foreground hover:text-[#17A2B8] transition-colors shrink-0"><ExternalLink className="w-4 h-4" /></button>
  </div>
);

export function FleetByCompany({ onOpenVehicle }) {
  const [overview, setOverview] = useState([]);
  const [pools, setPools] = useState([]);
  const [open, setOpen] = useState({});          // company_id -> bool
  const [vehByCo, setVehByCo] = useState({});     // company_id -> [veh] (lazy)
  const [loadingCo, setLoadingCo] = useState({});
  const [query, setQuery] = useState("");
  const [searchIndex, setSearchIndex] = useState(null); // all scoped vehicles (lazy, only when searching)

  const refresh = useCallback(() => {
    api.get("/companies/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/pools").then((r) => setPools(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const poolNamesByVeh = useMemo(() => {
    const m = {};
    pools.forEach((p) => (p.vehicle_ids || []).forEach((vid) => { (m[vid] = m[vid] || []).push(p.name); }));
    return m;
  }, [pools]);

  const loadCompany = useCallback(async (cid) => {
    if (vehByCo[cid]) return;
    setLoadingCo((s) => ({ ...s, [cid]: true }));
    try {
      const r = await api.get(`/vehicles?company_id=${encodeURIComponent(cid)}`);
      setVehByCo((s) => ({ ...s, [cid]: r.data }));
    } finally { setLoadingCo((s) => ({ ...s, [cid]: false })); }
  }, [vehByCo]);

  const toggle = (cid) => {
    setOpen((s) => ({ ...s, [cid]: !s[cid] }));
    if (!open[cid]) loadCompany(cid);
  };

  // Recherche : charge l'index complet (1 requête scopée) au premier caractère
  useEffect(() => {
    if (query.trim() && searchIndex === null) {
      api.get("/vehicles").then((r) => setSearchIndex(r.data)).catch(() => setSearchIndex([]));
    }
  }, [query, searchIndex]);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  const renderPoolGroups = (vehicles) => {
    const coPools = pools.filter((p) => vehicles.some((v) => (p.vehicle_ids || []).includes(v.id)));
    const inAnyPool = new Set();
    coPools.forEach((p) => (p.vehicle_ids || []).forEach((id) => inAnyPool.add(id)));
    const noPool = vehicles.filter((v) => !inAnyPool.has(v.id));
    return (
      <>
        {coPools.map((p) => {
          const members = vehicles.filter((v) => (p.vehicle_ids || []).includes(v.id));
          return (
            <div key={p.id} data-testid={`fleet-pool-group-${p.id}`}>
              <div className="px-3 py-1.5 bg-[#F5821F]/5 text-xs font-bold text-[#F5821F] flex items-center gap-1.5 border-t border-border"><Layers className="w-3 h-3" /> Groupe : {p.name} <span className="text-muted-foreground font-normal">· {members.length}</span></div>
              {members.map((v) => <VehicleRow key={v.id} v={v} poolNames={poolNamesByVeh[v.id] || []} onOpen={onOpenVehicle} />)}
            </div>
          );
        })}
        {noPool.length > 0 && (<>
          <div className="px-3 py-1.5 bg-black/5 text-xs font-bold text-muted-foreground border-t border-border">Sans groupe <span className="font-normal">· {noPool.length}</span></div>
          {noPool.map((v) => <VehicleRow key={v.id} v={v} poolNames={[]} onOpen={onOpenVehicle} />)}
        </>)}
      </>
    );
  };

  // Vue recherche : sociétés dont le nom matche OU contenant un véhicule matché (auto-ouvertes)
  const searchResults = useMemo(() => {
    if (!searching || !searchIndex) return [];
    return overview.map((co) => {
      const coMatch = (co.name || "").toLowerCase().includes(q);
      const vehs = searchIndex.filter((v) => v.company_id === co.company_id &&
        (coMatch || (v.name || "").toLowerCase().includes(q) || (v.plate || "").toLowerCase().includes(q)));
      return { co, vehs, coMatch };
    }).filter((r) => r.coMatch || r.vehs.length);
  }, [searching, searchIndex, overview, q]);

  return (
    <div data-testid="fleet-by-company">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-lg font-bold flex items-center gap-2"><Building2 className="w-5 h-5 brand-teal" /> Par société</h2>
        <div className="flex items-center gap-2 bg-secondary/40 border border-border rounded-md px-3 py-1.5 ml-auto w-72 max-w-full">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="fleet-search"
            placeholder="Société, véhicule ou immatriculation…" className="flex-1 bg-transparent text-sm outline-none min-w-0" />
          {query && <button onClick={() => setQuery("")} data-testid="fleet-search-clear" className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
        </div>
      </div>

      {searching ? (
        <div className="space-y-3" data-testid="fleet-search-results">
          {!searchIndex && <p className="text-sm text-muted-foreground">Recherche…</p>}
          {searchIndex && searchResults.length === 0 && <p className="text-sm text-muted-foreground" data-testid="fleet-search-empty">Aucun résultat pour « {query} ».</p>}
          {searchResults.map(({ co, vehs }) => (
            <div key={co.company_id} className="glass rounded-lg overflow-hidden" data-testid={`fleet-result-${co.company_id}`}>
              <div className="px-3 py-2 font-bold text-sm flex items-center gap-2 bg-black/5"><Building2 className="w-4 h-4 brand-teal" />{co.name} <span className="text-muted-foreground font-normal">· {vehs.length} résultat(s)</span></div>
              {vehs.map((v) => <VehicleRow key={v.id} v={v} poolNames={poolNamesByVeh[v.id] || []} onOpen={onOpenVehicle} />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2" data-testid="fleet-company-list">
          {overview.length === 0 && <p className="text-sm text-muted-foreground">Aucune société dans votre périmètre.</p>}
          {overview.map((co) => (
            <div key={co.company_id} className="glass rounded-lg overflow-hidden" data-testid={`fleet-company-${co.company_id}`}>
              <button onClick={() => toggle(co.company_id)} data-testid={`fleet-company-toggle-${co.company_id}`}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-black/5 transition-colors text-left">
                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open[co.company_id] ? "rotate-90" : ""}`} />
                <Building2 className="w-4 h-4 brand-teal shrink-0" />
                <span className="font-bold">{co.name}</span>
                <span className="text-sm text-muted-foreground">— {co.total} véhicule{co.total > 1 ? "s" : ""}</span>
                <span className="ml-auto text-xs flex items-center gap-3">
                  <span className="text-[#10B981] font-semibold">{co.active} actifs</span>
                  <span className="text-muted-foreground">{co.offline} au repos</span>
                </span>
              </button>
              {open[co.company_id] && (
                <div data-testid={`fleet-company-body-${co.company_id}`}>
                  {loadingCo[co.company_id] && <p className="px-4 py-3 text-xs text-muted-foreground">Chargement…</p>}
                  {vehByCo[co.company_id] && vehByCo[co.company_id].length === 0 && <p className="px-4 py-3 text-xs text-muted-foreground">Aucun véhicule.</p>}
                  {vehByCo[co.company_id] && vehByCo[co.company_id].length > 0 && renderPoolGroups(vehByCo[co.company_id])}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PoolManager() {
  const [pools, setPools] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, name, company_id, vehicle_ids}
  const [coVehicles, setCoVehicles] = useState([]);
  const [vehQuery, setVehQuery] = useState("");

  const load = useCallback(() => {
    api.get("/pools").then((r) => setPools(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const coName = (cid) => companies.find((c) => c.id === cid)?.name || "—";

  const openNew = () => { setEditing({ name: "", company_id: companies[0]?.id || "", vehicle_ids: [] }); setVehQuery(""); };
  const openEdit = (p) => { setEditing({ id: p.id, name: p.name, company_id: p.company_id, vehicle_ids: [...(p.vehicle_ids || [])] }); setVehQuery(""); };

  useEffect(() => {
    if (editing?.company_id) {
      api.get(`/vehicles?company_id=${encodeURIComponent(editing.company_id)}`).then((r) => setCoVehicles(r.data)).catch(() => setCoVehicles([]));
    } else setCoVehicles([]);
  }, [editing?.company_id]);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Nom du groupe requis"); return; }
    if (!editing.company_id) { toast.error("Société requise"); return; }
    const payload = { name: editing.name.trim(), company_id: editing.company_id, vehicle_ids: editing.vehicle_ids };
    try {
      if (editing.id) await api.put(`/pools/${editing.id}`, payload);
      else await api.post("/pools", payload);
      toast.success(editing.id ? "Groupe mis à jour" : "Groupe créé");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer le groupe « ${p.name} » ?`)) return;
    try { await api.delete(`/pools/${p.id}`); toast.success("Groupe supprimé"); load(); }
    catch { toast.error("Échec de la suppression"); }
  };

  const toggleVeh = (id) => setEditing((s) => ({ ...s, vehicle_ids: s.vehicle_ids.includes(id) ? s.vehicle_ids.filter((x) => x !== id) : [...s.vehicle_ids, id] }));

  const vq = vehQuery.trim().toLowerCase();
  const filteredVeh = coVehicles.filter((v) => !vq || (v.name || "").toLowerCase().includes(vq) || (v.plate || "").toLowerCase().includes(vq));

  return (
    <div data-testid="pool-manager">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2"><Layers className="w-5 h-5 brand-orange" /> Groupes de véhicules</h2>
        <button onClick={openNew} data-testid="pool-add" disabled={!companies.length}
          className="ml-auto flex items-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-50 text-white font-semibold px-3 py-2 rounded-md text-sm transition-colors"><Plus className="w-4 h-4" /> Nouveau groupe</button>
      </div>

      <div className="space-y-2" data-testid="pool-list">
        {pools.length === 0 && <p className="text-sm text-muted-foreground">Aucun groupe. Créez-en un pour regrouper des véhicules d'une même société.</p>}
        {pools.map((p) => (
          <div key={p.id} className="glass rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`pool-row-${p.id}`}>
            <Layers className="w-4 h-4 brand-orange shrink-0" />
            <span className="font-bold">{p.name}</span>
            <span className="text-xs text-muted-foreground">· {coName(p.company_id)}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#17A2B8]/15 text-[#2ec5db]">{(p.vehicle_ids || []).length} véhicule(s)</span>
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => openEdit(p)} data-testid={`pool-edit-${p.id}`} className="p-2 rounded-md hover:bg-black/5 text-muted-foreground hover:text-[#17A2B8] transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(p)} data-testid={`pool-delete-${p.id}`} className="p-2 rounded-md hover:bg-black/5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[1300] bg-black/50 flex items-center justify-center p-4" data-testid="pool-editor" onClick={() => setEditing(null)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-bold">{editing.id ? "Modifier le groupe" : "Nouveau groupe"}</h3>
              <button onClick={() => setEditing(null)} data-testid="pool-editor-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="label-mono block mb-1">Nom du groupe</label>
                <input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="pool-name"
                  className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm" placeholder="Ex. Techniciens" />
              </div>
              <div>
                <label className="label-mono block mb-1">Société</label>
                <select value={editing.company_id} data-testid="pool-company"
                  onChange={(e) => setEditing((s) => ({ ...s, company_id: e.target.value, vehicle_ids: [] }))}
                  className="w-full bg-secondary/40 border border-border rounded-md px-3 py-2 text-sm">
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">Un groupe ne peut contenir que des véhicules de cette société.</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="label-mono">Véhicules ({editing.vehicle_ids.length})</label>
                  <div className="flex items-center gap-1.5 bg-secondary/40 border border-border rounded-md px-2 py-1 ml-auto">
                    <Search className="w-3.5 h-3.5 text-muted-foreground" />
                    <input value={vehQuery} onChange={(e) => setVehQuery(e.target.value)} data-testid="pool-veh-search" placeholder="Rechercher…" className="bg-transparent text-xs outline-none w-40" />
                  </div>
                </div>
                <div className="border border-border rounded-md max-h-56 overflow-y-auto" data-testid="pool-veh-options">
                  {filteredVeh.length === 0 && <p className="px-3 py-4 text-xs text-muted-foreground text-center">Aucun véhicule</p>}
                  {filteredVeh.map((v) => {
                    const on = editing.vehicle_ids.includes(v.id);
                    return (
                      <button key={v.id} onClick={() => toggleVeh(v.id)} data-testid={`pool-veh-${v.id}`}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-black/5 transition-colors border-t border-border first:border-t-0">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-[#17A2B8] border-[#17A2B8]" : "border-border"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
                        <span className="flex-1 truncate">{v.name} <span className="text-muted-foreground">· {v.plate}</span></span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-black/5 transition-colors">Annuler</button>
              <button onClick={save} data-testid="pool-save" className="px-4 py-2 rounded-md text-sm font-semibold bg-[#17A2B8] hover:bg-[#1391a5] text-white transition-colors">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
